"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton, ProductSortDropdown, ProductsFilter } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { ChevronLeft, ChevronRight, ShoppingBag } from "lucide-react";
function ShopGridConsumer() {
  const {
    products,
    loading,
    error,
    refetch,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages
  } = useEcommerceProducts();
  if (loading) {
    return <div data-section-id="222211" className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="flex h-[400px] flex-col rounded-lg border border-border bg-card p-4 shadow-sm">
            <div className="mb-4 h-48 w-full animate-pulse rounded-md bg-muted"></div>
            <div className="mb-2 h-6 w-3/4 animate-pulse rounded bg-muted"></div>
            <div className="h-6 w-1/4 animate-pulse rounded bg-muted"></div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-card py-12 text-center shadow-sm">
        <p className="mb-4 text-muted-foreground font-default">Failed to load our fresh bakes.</p>
        <Button onClick={refetch} variant="outline" className="font-default">
          Try Again
        </Button>
      </div>;
  }
  if (products.length === 0) {
    return <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-card py-16 text-center shadow-sm">
        <ShoppingBag className="mb-4 size-12 text-primary opacity-50" />
        <h3 className="font-default text-2xl font-semibold text-foreground">
          No treats found
        </h3>
        <p className="mt-2 text-muted-foreground font-default">
          Try adjusting your filters to find your favorite pastry.
        </p>
      </div>;
  }
  return <div className="space-y-10">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {products.map((product, i) => <div key={product.id} data-index={i} className="group relative flex flex-col overflow-hidden rounded-lg border-none bg-card shadow-sm transition-all duration-500 hover:shadow-xl">
            <Link to={product.link} className="relative block aspect-square overflow-hidden bg-muted">
              {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" /> : <div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground">
                  <ShoppingBag className="size-12 opacity-20" />
                </div>}

              {}
              <div className="absolute inset-x-0 bottom-0 z-10 flex translate-y-4 justify-center bg-gradient-to-t from-foreground/60 to-transparent p-4 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                <AddToCartButton productId={product.id} className="w-full" buttonClassName="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full font-default font-medium uppercase tracking-widest text-xs" text="Order Now" showQuantity={false} />
              </div>
            </Link>

            <div className="flex flex-grow flex-col justify-between gap-3 p-5">
              <Link to={product.link} className="block">
                <h3 className="line-clamp-2 font-default text-xl font-semibold leading-tight text-foreground transition-colors hover:text-primary">
                  {product.title}
                </h3>
              </Link>
              <div className="font-default text-lg font-bold text-primary">
                ${product.price?.toFixed(2)}
              </div>
            </div>
          </div>)}
      </div>

      {}
      {(hasNext || hasPrev) && <div className="flex items-center justify-center gap-4 border-t border-border pt-8">
          <Button variant="outline" onClick={prevPage} disabled={!hasPrev} className="gap-2 rounded-full px-6 font-default border-primary text-primary hover:bg-primary hover:text-primary-foreground">
            <ChevronLeft className="size-4" />
            Previous
          </Button>
          <span className="font-default text-sm font-medium text-muted-foreground">
            Page {currentPage} of {totalPages}
          </span>
          <Button variant="outline" onClick={nextPage} disabled={!hasNext} className="gap-2 rounded-full px-6 font-default border-primary text-primary hover:bg-primary hover:text-primary-foreground">
            Next
            <ChevronRight className="size-4" />
          </Button>
        </div>}
    </div>;
}
export default function ShopContent() {
  const currentWPQueryParams = typeof window !== "undefined" && (window as any).wvcClient ? (window as any).wvcClient.getCurrentWPQueryParams() : {};
  return <section id="shop-content" className="bg-background py-16 md:py-24 text-foreground">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <EcommerceProductsProvider wp_query={currentWPQueryParams}>
          <div className="flex flex-col gap-8 lg:flex-row lg:gap-12">
            {}
            <aside className="hidden w-full shrink-0 lg:block lg:w-1/4">
              <div className="sticky top-24 space-y-8 rounded-lg border-none bg-secondary/30 p-6 shadow-sm">
                <h2 className="font-default text-xl font-bold text-foreground uppercase tracking-widest border-b border-border pb-4 mb-6">
                  Categories
                </h2>
                <ProductsFilter />
              </div>
            </aside>

            {}
            <div className="w-full lg:w-3/4">
              <div className="mb-8 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                <h1 className="font-default text-4xl font-bold text-foreground uppercase tracking-tighter md:text-5xl">
                  Artisan Pantry
                </h1>
                <div className="w-full sm:w-auto">
                  <ProductSortDropdown label="Sort by:" />
                </div>
              </div>

              {}
              <div className="mb-6 block lg:hidden">
                <div className="rounded-lg border-none bg-secondary/30 p-4 shadow-sm">
                  <h2 className="mb-4 font-default text-lg font-bold text-foreground uppercase tracking-widest">
                    Filters
                  </h2>
                  <ProductsFilter />
                </div>
              </div>

              <ShopGridConsumer />
            </div>
          </div>
        </EcommerceProductsProvider>
      </div>
    </section>;
}